/**
 * onGuard Watch catalog v1 (docs/superpowers/specs/2026-09-29-onguard-watch-design.md,
 * section 4): which Microsoft Entra directory audit records become watch
 * events, and how each is described.
 *
 * Pure: no I/O and no Node-only import. The portal reaches it through the
 * portal/backend/shared symlink; the Watch collector runs a byte-identical
 * copy in watch-lambda/shared/ (watch-lambda/tests/shared-copies.test.mjs).
 * Changing an entry, the ignore list or a confirmed property name is a
 * catalog release: bump CATALOG_VERSION, run watch-lambda/scripts/sync-shared.sh,
 * ship portal and collector. The portal accepts the current and the previous
 * version (portal/shared/watch-callback-schema.mjs).
 */

export const CATALOG_VERSION = 2;

export const SEVERITIES = Object.freeze(['low', 'medium', 'high', 'critical']);
// 'info' is the severity of an admin notice (isAdminNotice), of Microsoft first-party provisioning
// (app.registered, see microsoftProvisioning) and of a security info prompt that registered no method
// (self.security-info-added, see isMethodlessSecurityInfo). A collector may send it for nothing else: the portal
// bounds it per catalog id (watch-intake.mjs) and never mails an informational collector event.
export const COLLECTOR_SEVERITIES = Object.freeze(['info', ...SEVERITIES]);
const RANK = Object.freeze({ info: 0, low: 1, medium: 2, high: 3, critical: 4 });
const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);
export const severityRank = (s) => (typeof s === 'string' && hasOwn(RANK, s) ? RANK[s] : -1);
export const maxSeverity = (a, b) => (severityRank(a) >= severityRank(b) ? a : b);

export const CATEGORIES = Object.freeze(['my-account', 'groups', 'apps', 'roles', 'pim', 'tenant', 'guests', 'integrity']);
export const AUDIENCES = Object.freeze(['self', 'owners', 'guardians', 'admins']);
export const TARGET_KINDS = Object.freeze(['user', 'group', 'app', 'role', 'domain', 'policy', 'partner', 'device', 'tenant']);

// modifiedProperties names that need the TEST capture (spec 11.5, check 5).
// null means "not confirmed yet": the test or severity rule that needs the
// name stays off until a catalog release records it.
export const CONFIRMED_PROPERTY_NAMES = Object.freeze({
  proxyAddresses: 'ProxyAddresses', // seen in the 2026-09-29 capture
  membershipRule: null,
  otherMails: null,
  groupLicense: null,
  adminConsent: null,
});

// Critical roles (spec 4.1), by built-in role template id, falling back to
// the display name. Template ids from Microsoft Learn, "Microsoft Entra
// built-in roles".
export const CRITICAL_ROLES = Object.freeze({
  '62e90394-69f5-4237-9190-012177145e10': 'Global Administrator',
  'e8611ab8-c189-46e8-94e1-60213ab1f814': 'Privileged Role Administrator',
  '7be44c8a-adaf-4e2a-84d6-ab2649e08a13': 'Privileged Authentication Administrator',
  '194ae4cb-b126-40b2-bd5b-6091b380977d': 'Security Administrator',
  'b1be1c3e-b65d-4f19-8427-f6fa0d97feb9': 'Conditional Access Administrator',
  '9b895d92-2cd3-44c7-9d02-a6ac2d5ea5c3': 'Application Administrator',
  '158c047a-c907-4556-b7ef-446551a6b5f7': 'Cloud Application Administrator',
  '8ac3fc64-6eca-42ea-9e69-59f4c7b60eb2': 'Hybrid Identity Administrator',
});
const CRITICAL_NAMES = new Set(Object.values(CRITICAL_ROLES).map((n) => n.toLowerCase()));

export function isCriticalRole(arg) {
  const { templateId, displayName } = arg ?? {};
  const id = String(templateId ?? '').trim().toLowerCase();
  if (id && hasOwn(CRITICAL_ROLES, id)) return true;
  return CRITICAL_NAMES.has(String(displayName ?? '').trim().toLowerCase());
}

// Hyphen, non-breaking hyphen, figure dash, en dash, em dash, horizontal bar
// and minus sign all compare as a hyphen (graph-facts Q2, caveat 4).
const DASHES = /[\u2010-\u2015\u2212-]/g;

export function normalizeActivityName(name) {
  return String(name ?? '').replace(DASHES, '-').replace(/\s+/g, ' ').trim();
}

const lower = (s) => String(s ?? '').toLowerCase();

// ---- Record helpers ---------------------------------------------------------

export function targetsOf(record, type) {
  return (Array.isArray(record?.targetResources) ? record.targetResources : []).filter((t) => lower(t?.type) === lower(type));
}

export function firstTarget(record, type) {
  return targetsOf(record, type)[0] ?? null;
}

export function modifiedProperties(record, target = null) {
  const targets = target ? [target] : (Array.isArray(record?.targetResources) ? record.targetResources : []);
  return targets.flatMap((t) => (Array.isArray(t?.modifiedProperties) ? t.modifiedProperties : []));
}

export function propertyNamed(record, name) {
  if (!name) return null;
  const n = lower(name);
  return modifiedProperties(record).find((p) => lower(p?.displayName) === n) ?? null;
}

export function jsonValue(raw) {
  if (raw === null || raw === undefined) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
}

export function stringArray(raw) {
  const v = jsonValue(raw);
  return Array.isArray(v) && v.every((x) => typeof x === 'string') ? v : null;
}

export function scalarValue(raw) {
  const v = jsonValue(raw);
  if (Array.isArray(v)) return v.length > 0 && v[0] !== null ? String(v[0]) : null;
  if (v === null || v === undefined || typeof v === 'object') return null;
  return String(v);
}

const addressPart = (value) => String(value).replace(/^[A-Za-z0-9]+:/, '').trim();

/** The ProxyAddresses test of self.alias-added (spec 4.2). */
export function aliasChange(record) {
  const p = propertyNamed(record, CONFIRMED_PROPERTY_NAMES.proxyAddresses);
  if (!p) return { matches: false, aliases: [], parsed: false };
  const oldVal = String(p.oldValue ?? '').trim() || '[]';
  const newVal = String(p.newValue ?? '').trim() || '[]';
  const before = stringArray(oldVal);
  const after = stringArray(newVal);
  if (!before || !after) return { matches: true, aliases: [], parsed: false };
  const had = new Set(before.map((v) => lower(addressPart(v))));
  const added = [];
  for (const v of after) {
    const address = addressPart(v);
    if (!had.has(lower(address)) && !added.some((a) => lower(a) === lower(address))) added.push(address);
  }
  return { matches: added.length > 0, aliases: added.slice(0, 5), parsed: true };
}

/** Property names of one target, without the "Included Updated Properties" summary entry. */
export function changedPropertyNames(target) {
  return modifiedProperties(null, target)
    .map((p) => String(p?.displayName ?? '').trim())
    .filter((n) => n && lower(n) !== 'included updated properties');
}

const propertyChanged = (record, name) => Boolean(name) && propertyNamed(record, name) !== null;
const userTarget = (record) => firstTarget(record, 'User');
const groupTarget = (record) => firstTarget(record, 'Group');
const actorUserId = (record) => record?.initiatedBy?.user?.id ?? null;

function notDynamicGroup(record, ctx) {
  const g = groupTarget(record);
  const snap = g?.id && typeof ctx?.groupById === 'function' ? ctx.groupById(g.id) : null;
  return !(Array.isArray(snap?.groupTypes) && snap.groupTypes.includes('DynamicMembership'));
}

// ---- Monitored accounts (2026-10-25) -----------------------------------------
// The kinds of account an administrator can ask Watch to monitor, and the alert each one raises. The rule of each
// kind lives in watch-monitored.mjs (monitoredDecision); the catalog holds only the ids, so a record can never be
// mapped to them by activity.
export const MONITORED_TYPES = Object.freeze(['break-glass', 'service-account', 'vendor', 'former-employee', 'vip']);
export const MONITORED_EVENT_IDS = Object.freeze({
  'break-glass': 'monitored.break-glass-sign-in',
  'service-account': 'monitored.service-account-sign-in',
  vendor: 'monitored.vendor-sign-in-outside-window',
  'former-employee': 'monitored.former-employee-sign-in',
  vip: 'monitored.vip-risky-sign-in',
});
export const MONITORED_SEVERITY = Object.freeze({ 'break-glass': 'critical', 'service-account': 'high', vendor: 'high', 'former-employee': 'high', vip: 'critical' });
export const MONITORED_TYPE_LABELS = Object.freeze({
  'break-glass': 'Break-glass (emergency access)',
  'service-account': 'Service account',
  vendor: 'Vendor or temporary',
  'former-employee': 'Former employee or disabled',
  vip: 'Executive / VIP',
});
const MONITORED_TYPE_BY_ID = new Map(Object.entries(MONITORED_EVENT_IDS).map(([type, id]) => [id, type]));
/** The monitored account type an alert id belongs to, or null for any other id. */
export const monitoredTypeOf = (catalogId) => (typeof catalogId === 'string' && MONITORED_TYPE_BY_ID.has(catalogId) ? MONITORED_TYPE_BY_ID.get(catalogId) : null);
export const isMonitoredEventId = (catalogId) => monitoredTypeOf(catalogId) !== null;

// ---- Entries ----------------------------------------------------------------

const act = (category, names) => names.map((name) => Object.freeze({ name: normalizeActivityName(name), category }));

function entry({ id, category, audiences, target, activities, detection = 'live', test = null, snapshotDiff = null, source = 'audit' }) {
  return Object.freeze({ id, category, detection, source, audiences: Object.freeze(audiences), target, activities: Object.freeze(activities), test, snapshotDiff });
}

const ME = { category: 'my-account', audiences: ['self'], target: 'user' };
const GROUP = { category: 'groups', audiences: ['owners'], target: 'group' };
const APP = { category: 'apps', audiences: ['guardians'], target: 'app' };
const ROLE = { category: 'roles', audiences: ['guardians'], target: 'role' };
const TENANT = { category: 'tenant', audiences: ['guardians'] };

// Order matters only between entries that share an activity: a pending entry
// sits before its generic sibling so that confirming it later takes
// precedence without reordering.
export const CATALOG = Object.freeze([
  // 4.2 My account
  entry({ ...ME, id: 'self.security-info-added', activities: act('UserManagement', ['User registered security info', 'Admin registered security info']) }),
  entry({ ...ME, id: 'self.security-info-removed', activities: act('UserManagement', ['User deleted security info', 'Admin deleted security info']) }),
  entry({ ...ME, id: 'self.security-info-changed', activities: act('UserManagement', ['User changed default security info', 'User updated security info', 'Admin updated security info']) }),
  entry({ ...ME, id: 'self.per-user-mfa-changed', activities: act('UserManagement', ['Update per-user multifactor authentication state', 'Disable Strong Authentication', 'Enable Strong Authentication']) }),
  entry({
    ...ME,
    id: 'self.password-reset-by-other',
    activities: act('UserManagement', ['Reset password', 'Reset password (by admin)', 'Set force change user password']),
    test: (r) => {
      const t = userTarget(r);
      return Boolean(t?.id) && t.id !== actorUserId(r);
    },
  }),
  entry({ ...ME, id: 'self.alternate-email-changed', detection: 'pending', activities: act('UserManagement', ['Update user']), test: (r) => propertyChanged(r, CONFIRMED_PROPERTY_NAMES.otherMails) }),
  entry({ ...ME, id: 'self.alias-added', activities: act('UserManagement', ['Update user']), test: (r) => aliasChange(r).matches }),
  entry({ ...ME, id: 'self.account-state-changed', activities: act('UserManagement', ['Disable account', 'Enable account']) }),
  entry({ ...ME, id: 'self.device-registered', target: 'device', activities: act('Device', ['Register device', 'Add device']) }),
  // ENTRAID-346: a Microsoft Entra ID Protection risk detection, read by the risk stage of the Watch run. No audit activity,
  // so no audit record can ever map to it (candidateEntries matches activities only).
  entry({ ...ME, audiences: ['self', 'guardians'], id: 'self.risky-sign-in', source: 'risk', activities: [] }),
  // Monitored accounts (2026-10-25): a sign-in of an account an administrator confirmed as monitored, read from the Entra
  // sign-in log by the monitored stage of the Watch run (watch-lambda/monitored-stage.mjs). No audit activity, so no audit
  // record can map to them. Guardians only: the account itself is never told (an attacker holding it would read the notice).
  ...MONITORED_TYPES.map((type) => entry({ category: 'tenant', audiences: ['guardians'], target: 'user', id: MONITORED_EVENT_IDS[type], source: 'sign-in', activities: [] })),

  // 4.3 My resources: groups
  entry({ ...GROUP, id: 'group.member-added', snapshotDiff: 'group-memberships', activities: act('GroupManagement', ['Add member to group']), test: notDynamicGroup }),
  entry({ ...GROUP, id: 'group.member-removed', snapshotDiff: 'group-memberships', activities: act('GroupManagement', ['Remove member from group']), test: notDynamicGroup }),
  entry({ ...GROUP, id: 'group.owner-changed', snapshotDiff: 'group-memberships', activities: act('GroupManagement', ['Add owner to group', 'Remove owner from group']) }),
  entry({ ...GROUP, id: 'group.license-changed', detection: 'pending', activities: act('GroupManagement', ['Update group']), test: (r) => propertyChanged(r, CONFIRMED_PROPERTY_NAMES.groupLicense) }),
  entry({ ...GROUP, id: 'group.settings-changed', activities: act('GroupManagement', ['Update group', 'Update group settings']), test: (r) => changedPropertyNames(groupTarget(r)).length > 0 }),
  entry({ ...GROUP, id: 'group.deleted-or-restored', activities: act('GroupManagement', ['Delete group', 'Hard Delete group', 'Restore group']) }),

  // 4.4 My resources: apps (guardians in Phase 1)
  entry({ ...APP, id: 'app.credential-added', activities: act('ApplicationManagement', ['Update application - Certificates and secrets management', 'Add service principal credentials']) }),
  entry({ ...APP, id: 'app.permission-granted', activities: act('ApplicationManagement', ['Add app role assignment to service principal']) }),
  entry({ ...APP, id: 'app.consent-granted', activities: act('ApplicationManagement', ['Consent to application', 'Add delegated permission grant']) }),
  entry({ ...APP, id: 'app.owner-changed', activities: act('ApplicationManagement', ['Add owner to application', 'Remove owner from application', 'Add owner to service principal', 'Remove owner from service principal']) }),
  entry({ ...APP, id: 'app.registered', activities: act('ApplicationManagement', ['Add application', 'Add service principal']) }),

  // 4.5 Tenant guardians
  entry({
    ...ROLE,
    id: 'role.assigned',
    snapshotDiff: 'user-roles',
    activities: act('RoleManagement', [
      'Add member to role', 'Add eligible member to role', 'Add scoped member to role',
      'Add member to role outside of PIM (permanent)', 'Add eligible member to role in PIM completed (permanent)',
      'Add eligible member to role in PIM completed (timebound)', 'Add role assignment to role definition',
      'Add EligibleRoleAssignment to RoleDefinition',
    ]),
  }),
  entry({
    ...ROLE,
    id: 'role.removed',
    snapshotDiff: 'user-roles',
    activities: act('RoleManagement', [
      'Remove member from role', 'Remove eligible member from role', 'Remove scoped member from role',
      'Remove role assignment from role definition', 'Remove EligibleRoleAssignment from RoleDefinition',
      'Remove eligible member from role in PIM completed (permanent)', 'Remove eligible member from role in PIM completed (timebound)',
    ]),
  }),
  entry({ ...ROLE, category: 'pim', id: 'pim.activation', activities: act('RoleManagement', ['Add member to role completed (PIM activation)']) }),
  entry({
    ...TENANT,
    id: 'tenant.domain-changed',
    target: 'domain',
    snapshotDiff: 'domains',
    activities: act('DirectoryManagement', ['Add verified domain', 'Add unverified domain', 'Verify domain', 'Remove verified domain', 'Update domain']),
    test: (r) => normalizeActivityName(r?.activityDisplayName) !== 'Update domain' || modifiedProperties(r).length > 0,
  }),
  entry({ ...TENANT, id: 'tenant.federation-changed', target: 'domain', activities: act('DirectoryManagement', ['Set domain authentication', 'Set federation settings on domain']) }),
  entry({
    ...TENANT,
    id: 'tenant.conditional-access-changed',
    target: 'policy',
    snapshotDiff: 'conditional-access',
    activities: act('Policy', [
      'Add Conditional Access policy', 'Update Conditional Access policy', 'Delete Conditional Access policy',
      'Add named location', 'Update named location', 'Delete named location',
      'Update security defaults', 'Update continuous access evaluation',
    ]),
  }),
  entry({
    ...TENANT,
    id: 'tenant.auth-methods-policy-changed',
    target: 'policy',
    activities: act('ApplicationManagement', [
      'Authentication Methods Policy Update', 'Authentication Methods Policy Reset',
      'Authentication Strength Policy Create', 'Authentication Strength Policy Update', 'Authentication Strength Policy Delete',
    ]),
  }),
  entry({ ...TENANT, id: 'tenant.partner-changed', target: 'partner', activities: act('DirectoryManagement', ['Add partner to company', 'Remove partner from company', 'Set Partnership', 'Promote company to partner', 'Demote partner']) }),
  entry({ category: 'guests', audiences: ['guardians'], target: 'user', id: 'guest.invited', activities: act('UserManagement', ['Invite external user', 'Invite external user with reset invitation status']) }),
]);

const BY_ID = new Map(CATALOG.map((e) => [e.id, e]));

export function catalogEntry(id) {
  return typeof id === 'string' && BY_ID.has(id) ? BY_ID.get(id) : null;
}

// The versioned ignore list (spec 4.8). `service` compares case-insensitively.
const PIM_INTERMEDIATE = [
  'Add member to role requested (PIM activation)', 'Add member to role approval requested (PIM activation)',
  'Add member to role request approved (PIM activation)', 'Add member to role request denied (PIM activation)',
  'Add member to role canceled (PIM activation)', 'Remove member from role (PIM activation expired)',
  'Remove member from role completed (PIM deactivate)', 'Remove member from role requested (PIM deactivate)',
  'PIM activation request expired',
];
export const IGNORED = Object.freeze([
  { name: 'Import', service: 'Account Provisioning' },
  { name: 'Execution', service: 'Account Provisioning' },
  { name: 'Get passkey creation options' },
  { name: 'Settings_GetSettingsAsync' },
  { name: 'GroupLifecyclePolicies_Get' },
  { name: 'User registered all required security info' },
  ...PIM_INTERMEDIATE.map((name) => ({ name })),
  // PIM for Groups and Azure resources use the same activity name (spec 4.5).
  { name: 'Add member to role completed (PIM activation)', category: 'GroupManagement' },
  { name: 'Add member to role completed (PIM activation)', category: 'ResourceManagement' },
].map((r) => Object.freeze({ ...r, name: normalizeActivityName(r.name) })));

export function isIgnored(record) {
  const name = normalizeActivityName(record?.activityDisplayName);
  const service = lower(record?.loggedByService);
  const category = String(record?.category ?? '');
  return IGNORED.some((r) => r.name === name
    && (r.service === undefined || lower(r.service) === service)
    && (r.category === undefined || r.category === category));
}

export function recordSignature(record) {
  return {
    loggedByService: String(record?.loggedByService ?? '').slice(0, 200),
    category: String(record?.category ?? '').slice(0, 200),
    activityDisplayName: normalizeActivityName(record?.activityDisplayName).slice(0, 200),
    operationType: String(record?.operationType ?? '').slice(0, 200),
  };
}

export function candidateEntries(record) {
  const name = normalizeActivityName(record?.activityDisplayName);
  const category = String(record?.category ?? '');
  return CATALOG.filter((e) => e.activities.some((a) => a.name === name && a.category === category));
}

/** Exactly one class per record (spec 4.8): mapped, ignored or unclassified. */
export function classifyRecord(record, ctx = {}) {
  if (isIgnored(record)) return { kind: 'ignored', reason: 'ignore-list' };
  const candidates = candidateEntries(record);
  if (candidates.length === 0) return { kind: 'unclassified', signature: recordSignature(record) };
  for (const e of candidates) {
    if (e.detection !== 'live') continue;
    if (!e.test || e.test(record, ctx)) return { kind: 'mapped', entry: e };
  }
  return { kind: 'ignored', reason: candidates.every((e) => e.detection !== 'live') ? 'pending' : 'failed-test' };
}

// ---- Integrity notices detected from audit records (spec 4.6) --------------

export const INTEGRITY_FROM_AUDIT = Object.freeze([
  Object.freeze({ id: 'integrity.guardian-changed', activities: Object.freeze(act('UserManagement', ['Disable account', 'Delete user', 'Hard Delete user', 'Restore user'])) }),
]);

export function integrityMatches(record, ctx = {}) {
  const out = [];
  const name = normalizeActivityName(record?.activityDisplayName);
  const category = String(record?.category ?? '');
  const guardians = ctx.guardianUserIds instanceof Set ? ctx.guardianUserIds : new Set();
  const target = userTarget(record);
  const guardianRule = INTEGRITY_FROM_AUDIT[0];
  if (target?.id && guardians.has(target.id) && guardianRule.activities.some((a) => a.name === name && a.category === category)) {
    out.push(guardianRule.id);
  }
  const sp = ctx.onguardServicePrincipalId;
  if (sp && Array.isArray(record?.targetResources) && record.targetResources.some((t) => t?.id === sp)) out.push('integrity.onguard-access-changed');
  return out;
}

// ---- Evaluation (spec 4.1 to 4.6) --------------------------------------------

const nameOr = (...values) => {
  for (const v of values) {
    const s = typeof v === 'string' ? v.trim() : '';
    if (s) return s;
  }
  return null;
};

const isSyncAccount = (upn) => /^(sync_|adtoaadsyncserviceaccount@)/i.test(String(upn ?? ''));
const isSyncApp = (name) => /(azure ad connect|entra connect|cloud sync)/i.test(String(name ?? ''));

/** "Who did it", the default rule: user, else app, else the system. Never an id. */
export function actorOf(record, ctx = {}) {
  const u = record?.initiatedBy?.user;
  if (u && (u.id || u.userPrincipalName)) {
    const snap = u.id && typeof ctx.userById === 'function' ? ctx.userById(u.id) : null;
    const upn = nameOr(u.userPrincipalName, snap?.userPrincipalName);
    return {
      kind: 'user',
      directoryId: u.id ?? null,
      displayName: nameOr(u.displayName, snap?.displayName, upn) ?? 'Unknown user',
      userPrincipalName: upn,
      onPremSync: isSyncAccount(upn),
    };
  }
  const a = record?.initiatedBy?.app;
  if (a && (a.appId || a.displayName || a.servicePrincipalId)) {
    const snap = a.appId && typeof ctx.servicePrincipalByAppId === 'function' ? ctx.servicePrincipalByAppId(a.appId) : null;
    const name = nameOr(a.displayName, snap?.displayName) ?? 'An application';
    return { kind: 'app', directoryId: a.appId ?? null, displayName: name, userPrincipalName: null, onPremSync: isSyncApp(name) };
  }
  return { kind: 'system', directoryId: null, displayName: 'Microsoft Entra (system)', userPrincipalName: null, onPremSync: false };
}

function userRef(t, ctx) {
  if (!t || (!t.id && !t.userPrincipalName)) return null;
  const snap = t.id && typeof ctx.userById === 'function' ? ctx.userById(t.id) : null;
  return { kind: 'user', directoryId: t.id ?? null, displayName: nameOr(t.displayName, snap?.displayName, t.userPrincipalName, snap?.userPrincipalName) ?? 'a user' };
}

const valueOf = (record, name) => scalarValue(propertyNamed(record, name)?.newValue) ?? scalarValue(propertyNamed(record, name)?.oldValue);

function roleOf(record) {
  const name = nameOr(valueOf(record, 'Role.DisplayName'), firstTarget(record, 'Role')?.displayName);
  const templateId = nameOr(valueOf(record, 'Role.TemplateId'));
  return { name, templateId, critical: isCriticalRole({ templateId, displayName: name }) };
}

const PLACEHOLDER = Object.freeze({ user: 'a user', group: 'a group', app: 'an application', role: 'a directory role', domain: 'a domain', policy: 'a policy', partner: 'a partner', device: 'a device', tenant: 'the tenant' });

function genericRef(record, kind, preferredType) {
  const all = Array.isArray(record?.targetResources) ? record.targetResources : [];
  // A preferred type is strict: with none of that type present the ref stays a
  // placeholder, never a resource of another type (a user is not a device).
  const t = preferredType ? firstTarget(record, preferredType) : (all.find((x) => nameOr(x?.displayName)) || all[0] || null);
  return { kind, directoryId: t?.id ?? null, displayName: nameOr(t?.displayName, t?.userPrincipalName) ?? PLACEHOLDER[kind] };
}

function resolveTarget(record, entry, ctx) {
  const userT = firstTarget(record, 'User');
  switch (entry.target) {
    case 'user': {
      const u = userRef(userT, ctx);
      return { target: u ?? { kind: 'user', directoryId: null, displayName: PLACEHOLDER.user }, subject: entry.id === 'guest.invited' ? null : u };
    }
    case 'device': {
      const initiator = record?.initiatedBy?.user ? { ...record.initiatedBy.user } : null;
      return { target: genericRef(record, 'device', 'Device'), subject: userRef(userT, ctx) ?? userRef(initiator, ctx) };
    }
    case 'group': {
      const g = firstTarget(record, 'Group');
      const snap = g?.id && typeof ctx.groupById === 'function' ? ctx.groupById(g.id) : null;
      const name = nameOr(g?.displayName, valueOf(record, 'Group.DisplayName'), snap?.displayName) ?? PLACEHOLDER.group;
      return { target: { kind: 'group', directoryId: g?.id ?? null, displayName: name }, subject: userRef(userT, ctx) };
    }
    case 'app': {
      const t = firstTarget(record, 'Application') ?? firstTarget(record, 'ServicePrincipal');
      return { target: { kind: 'app', directoryId: t?.id ?? null, displayName: nameOr(t?.displayName) ?? PLACEHOLDER.app }, subject: null };
    }
    case 'role': {
      const r = roleOf(record);
      return { target: { kind: 'role', directoryId: r.templateId, displayName: r.name ?? PLACEHOLDER.role }, subject: userRef(userT, ctx) };
    }
    default:
      return { target: genericRef(record, entry.target, entry.target === 'policy' ? 'Policy' : null), subject: null };
  }
}

const sameText = (a, b) => {
  const x = lower(a).trim();
  return x !== '' && x === lower(b).trim();
};

/**
 * Whether the actor is the user the change benefits. The witness rule fails
 * closed, so the comparison is case-insensitive and never needs both sides to
 * carry a directory id: when either id is missing the user principal names
 * (actor versus the raw target user of the record; a Ref carries no UPN) decide.
 * Two missing values are never equal.
 */
function actorIsTarget(actor, subject, rawUser) {
  if (actor?.kind !== 'user' || !subject) return false;
  if (nameOr(actor.directoryId) && nameOr(subject.directoryId)) return sameText(actor.directoryId, subject.directoryId);
  return sameText(actor.userPrincipalName, rawUser?.userPrincipalName);
}

/**
 * Microsoft's own tenants, the values of appOwnerOrganizationId on a first-party service principal.
 * Source: https://learn.microsoft.com/en-us/troubleshoot/entra/entra-id/governance/verify-first-party-apps-sign-in
 * (f8cdef31... is "the Microsoft Service's Microsoft Entra tenant ID", 72f988bf... the Microsoft tenant that owns
 * Microsoft tenant-owned applications such as Graph Explorer).
 */
export const MICROSOFT_TENANT_IDS = Object.freeze(['f8cdef31-a31e-4b4a-93e4-5f571e91255a', '72f988bf-86f1-41af-91ab-2d7cd011db47']);
export const isMicrosoftTenantId = (v) => typeof v === 'string' && MICROSOFT_TENANT_IDS.includes(v);

/**
 * The two service principal object ids that decide whether an "Add service principal" record is Microsoft
 * provisioning: the application that did it and the one it added. Null for any other record, for a user
 * initiator, or when either id is missing (the answer is then "not provisioning": fail safe).
 */
export function provisioningServicePrincipalIds(record) {
  if (normalizeActivityName(record?.activityDisplayName) !== 'Add service principal') return null;
  if (record?.initiatedBy?.user) return null;
  const initiator = record?.initiatedBy?.app?.servicePrincipalId;
  const added = firstTarget(record, 'ServicePrincipal')?.id;
  return typeof initiator === 'string' && initiator !== '' && typeof added === 'string' && added !== '' ? { initiator, added } : null;
}

/**
 * The signal `flags.microsoftProvisioning`: the two verified owner tenant ids, or null. Non-null only when the record
 * is "Add service principal", done by an application, and both that application's and the added service principal's
 * appOwnerOrganizationId (ctx.appOwnerOrganizationId, resolved by the collector from Microsoft Graph) are exactly
 * Microsoft's own tenant ids. Any missing answer or error gives null. Consent grants, app role assignments and
 * credentials are other activities and never reach this check. The portal re-checks the two ids (watch-intake.mjs).
 */
export function microsoftProvisioning(record, ctx = {}) {
  const ids = provisioningServicePrincipalIds(record);
  if (!ids || typeof ctx.appOwnerOrganizationId !== 'function') return null;
  try {
    const initiatorOwnerOrganizationId = ctx.appOwnerOrganizationId(ids.initiator);
    const addedOwnerOrganizationId = ctx.appOwnerOrganizationId(ids.added);
    return isMicrosoftTenantId(initiatorOwnerOrganizationId) && isMicrosoftTenantId(addedOwnerOrganizationId)
      ? { initiatorOwnerOrganizationId, addedOwnerOrganizationId }
      : null;
  } catch {
    return null;
  }
}

/** True when a flags.microsoftProvisioning value carries exactly two Microsoft tenant ids. */
export const isMicrosoftProvisioningFlag = (f) => f !== null && typeof f === 'object' && isMicrosoftTenantId(f.initiatorOwnerOrganizationId) && isMicrosoftTenantId(f.addedOwnerOrganizationId);

export const SELF_BENEFIT_IDS = Object.freeze(['group.member-added', 'group.member-removed', 'group.owner-changed', 'role.assigned']);

export function evaluateRecord(record, entry, ctx = {}) {
  const activity = normalizeActivityName(record?.activityDisplayName);
  const { target, subject } = resolveTarget(record, entry, ctx);
  const actor = entry.id === 'pim.activation' && subject
    ? { kind: 'user', directoryId: subject.directoryId, displayName: subject.displayName, userPrincipalName: ctx.userById?.(subject.directoryId)?.userPrincipalName ?? null, onPremSync: false }
    : actorOf(record, ctx);
  const detail = { changedProperties: [], addedAliases: [], roleName: null, aggregateCount: 1, activity };
  const audiences = new Set(entry.audiences);
  const extraOwnerIds = [];
  const groupSnap = entry.target === 'group' && target.directoryId && typeof ctx.groupById === 'function' ? ctx.groupById(target.directoryId) : null;
  const roleAssignable = groupSnap?.isAssignableToRole === true;
  const watched = entry.target === 'group' && Boolean(target.directoryId) && (roleAssignable || ctx.isWatchedGroup?.(target.directoryId) === true);
  const actorIsSubject = actorIsTarget(actor, subject, firstTarget(record, 'User'));
  const selfBenefiting = SELF_BENEFIT_IDS.includes(entry.id) && actorIsSubject
    && !(entry.id === 'group.owner-changed' && activity === 'Remove owner from group');
  let severity;
  let microsoft = null;
  let noMethodRecorded = false;

  switch (entry.id) {
    case 'self.security-info-added':
      // ENTRAID-347: the method-less record Entra writes when the security info prompt closes registered nothing.
      // Every real registration, by the person or by an admin, stays high, whatever the method: the record carries
      // no device id, so a Windows Hello for Business or device-bound passkey from a managed device cannot be told
      // apart from one registered elsewhere, and a synced passkey can be registered from any browser.
      noMethodRecorded = actorIsSubject && isMethodlessSecurityInfo(record);
      severity = noMethodRecorded ? 'info' : 'high';
      break;
    case 'self.security-info-removed':
    case 'self.password-reset-by-other':
      severity = 'high';
      break;
    case 'self.security-info-changed':
    case 'self.alternate-email-changed':
      severity = 'medium';
      break;
    case 'self.per-user-mfa-changed':
      severity = activity === 'Disable Strong Authentication' ? 'high' : 'medium';
      if (activity === 'Disable Strong Authentication') audiences.add('guardians');
      break;
    case 'self.alias-added':
      severity = 'medium';
      detail.addedAliases = aliasChange(record).aliases;
      break;
    case 'self.account-state-changed':
      severity = activity === 'Disable account' ? 'high' : 'medium';
      if (subject?.directoryId && ctx.userHoldsDirectoryRole?.(subject.directoryId) === true) audiences.add('guardians');
      break;
    case 'self.device-registered':
      severity = 'low';
      break;
    case 'group.member-added':
    case 'group.member-removed':
      severity = watched || selfBenefiting ? 'high' : 'medium';
      if (roleAssignable || selfBenefiting) audiences.add('guardians');
      break;
    case 'group.owner-changed':
      severity = 'high';
      if (roleAssignable || selfBenefiting) audiences.add('guardians');
      if (subject?.directoryId) extraOwnerIds.push(subject.directoryId);
      break;
    case 'group.settings-changed': {
      detail.changedProperties = changedPropertyNames(firstTarget(record, 'Group')).slice(0, 50);
      const rule = CONFIRMED_PROPERTY_NAMES.membershipRule;
      const ruleChanged = Boolean(rule) && detail.changedProperties.some((n) => lower(n) === lower(rule));
      severity = ruleChanged || watched ? 'high' : 'medium';
      break;
    }
    case 'group.deleted-or-restored':
      severity = 'high';
      if (roleAssignable) audiences.add('guardians');
      break;
    case 'group.license-changed':
      severity = 'low';
      break;
    case 'app.credential-added':
    case 'app.permission-granted':
      severity = 'high';
      break;
    case 'app.consent-granted': {
      const flag = CONFIRMED_PROPERTY_NAMES.adminConsent;
      severity = flag && lower(valueOf(record, flag)) === 'true' ? 'critical' : 'high';
      break;
    }
    case 'app.registered':
      // Microsoft auto-provisions its own service principals: recorded and visible, never mailed.
      microsoft = microsoftProvisioning(record, ctx);
      severity = microsoft ? 'info' : 'medium';
      break;
    case 'app.owner-changed':
    case 'guest.invited':
      severity = 'medium';
      break;
    case 'role.assigned': {
      const r = roleOf(record);
      severity = r.critical ? 'critical' : 'high';
      if (subject?.directoryId && !actorIsSubject) {
        audiences.add('self');
        detail.roleName = r.name;
      }
      break;
    }
    case 'role.removed':
      severity = 'medium';
      if (subject?.directoryId) {
        audiences.add('self');
        detail.roleName = roleOf(record).name;
      }
      break;
    case 'pim.activation':
      severity = roleOf(record).critical ? 'critical' : 'high';
      break;
    case 'tenant.domain-changed':
    case 'tenant.federation-changed':
    case 'tenant.partner-changed':
      severity = 'critical';
      break;
    case 'tenant.conditional-access-changed':
      severity = activity === 'Delete Conditional Access policy' || activity === 'Update security defaults' ? 'critical' : 'high';
      break;
    case 'tenant.auth-methods-policy-changed':
      severity = 'high';
      break;
    default:
      severity = 'medium';
  }
  if (selfBenefiting) {
    severity = maxSeverity(severity, 'high');
    audiences.add('guardians');
  }
  return {
    catalogId: entry.id,
    category: entry.category,
    severity,
    actor,
    target,
    subject,
    detail,
    flags: { watchedResource: watched, selfBenefiting, ...(microsoft ? { microsoftProvisioning: microsoft } : {}), ...(noMethodRecorded ? { noMethodRecorded: true } : {}) },
    audiences: [...audiences],
    extraOwnerIds,
    excludeRecipientIds: [],
  };
}

// Notices the portal raises on its own, plus the two integrity notices a
// collector detects in the audit log. All are category 'integrity': always
// immediate, never in a digest, never silenced by preferences (spec 4.6, 7.2).
export const PORTAL_NOTICES = Object.freeze({
  'integrity.collector-silent': Object.freeze({ severity: 'critical' }),
  'integrity.collector-blind': Object.freeze({ severity: 'critical' }),
  'integrity.collector-lagging': Object.freeze({ severity: 'medium' }),
  'integrity.watch-config-changed': Object.freeze({ severity: 'high' }),
  'integrity.guardian-changed': Object.freeze({ severity: 'high' }),
  'integrity.onguard-access-changed': Object.freeze({ severity: 'critical' }),
  'integrity.volume-capped': Object.freeze({ severity: 'medium' }),
  'integrity.recipient-complained': Object.freeze({ severity: 'medium' }),
  // Monitored accounts (review M3): Watch could not read every sign-in of a break-glass account on a run.
  'integrity.break-glass-unread': Object.freeze({ severity: 'high' }),
  'case.opened': Object.freeze({ severity: 'high' }),
  'case.updated': Object.freeze({ severity: 'high' }),
  // A customer admin marked an unanswered event as expected: the guardians are told, so the closing has a witness.
  'event.marked-expected': Object.freeze({ severity: 'high' }),
  'prefs.changed': Object.freeze({ severity: 'low' }),
});

// Admin notices: the tamper-evidence records Watch raises about itself (settings changed, guardian added or
// removed, case opened, case status changed, suspension or resume, which the settings notice carries).
// They are not directory events: severity Info, no "Was this expected?" question, no answer links, no
// answer state, they never open a case and never cause another notice. The class follows the catalog id and
// the portal as author, never a stored field, so an old row and a collector event cannot change it.
// Delivery policy is unchanged (noticeMeta().severity stays the policy severity): they are still immediate.
export const ADMIN_NOTICE_SEVERITY = 'info';
export const ADMIN_NOTICE_IDS = Object.freeze(['integrity.watch-config-changed', 'integrity.guardian-changed', 'case.opened', 'case.updated', 'event.marked-expected']);
export const isAdminNotice = (event) => event?.detectedBy === 'portal' && typeof event?.catalogId === 'string' && ADMIN_NOTICE_IDS.includes(event.catalogId);

export function noticeMeta(id) {
  return typeof id === 'string' && hasOwn(PORTAL_NOTICES, id) ? { id, category: 'integrity', severity: PORTAL_NOTICES[id].severity } : null;
}

// The only integrity ids a collector may detect in the audit log (see integrityMatches).
const AUDIT_INTEGRITY_IDS = Object.freeze([...INTEGRITY_FROM_AUDIT.map((e) => e.id), 'integrity.onguard-access-changed']);

export const COLLECTOR_EVENT_IDS = Object.freeze([...CATALOG.map((e) => e.id), ...AUDIT_INTEGRITY_IDS]);

// ---- Risky sign-in (ENTRAID-346) ---------------------------------------------

export const RISK_EVENT_ID = 'self.risky-sign-in';
export const RISK_LEVELS = Object.freeze(['low', 'medium', 'high', 'hidden']);

/** Spec section 5: a role holder or a high level is critical, medium (and hidden) is high, low is low. Null for anything else. */
export function riskSeverity({ riskLevel, holdsDirectoryRole } = {}) {
  if (holdsDirectoryRole === true || riskLevel === 'high') return 'critical';
  if (riskLevel === 'medium' || riskLevel === 'hidden') return 'high';
  if (riskLevel === 'low') return 'low';
  return null;
}

const RISK_TYPE_GROUPS = Object.freeze({
  travel: ['unlikelyTravel', 'impossibleTravel', 'mcasImpossibleTravel'],
  'anonymous-ip': ['anonymizedIPAddress'],
  'suspicious-ip': ['maliciousIPAddress', 'riskyIPAddress', 'suspiciousIPAddress', 'malwareInfectedIPAddress'],
  unfamiliar: ['unfamiliarFeatures'],
  'password-spray': ['passwordSpray'],
  token: ['anomalousToken', 'tokenIssuerAnomaly'],
  'attacker-in-the-middle': ['attackerinTheMiddle'],
  'suspicious-browser': ['suspiciousBrowser'],
  'new-country': ['newCountry'],
  'threat-intel': ['investigationsThreatIntelligence'],
  'admin-confirmed': ['adminConfirmedUserCompromised'],
});
export const RISK_TYPE_LABELS = Object.freeze({
  travel: 'Sign-in from an unlikely location',
  'anonymous-ip': 'Sign-in from an anonymous IP address',
  'suspicious-ip': 'Sign-in from a suspicious IP address',
  unfamiliar: 'Unfamiliar sign-in properties',
  'password-spray': 'Password spray attack',
  token: 'Unusual sign-in token',
  'attacker-in-the-middle': 'Attacker in the middle',
  'suspicious-browser': 'Suspicious browser',
  'new-country': 'Sign-in from a new country',
  'threat-intel': 'Microsoft threat intelligence',
  'admin-confirmed': 'Confirmed compromised by an administrator',
  generic: 'Other risk detection',
});

/** The group of a Graph riskEventType; 'generic' for anything unknown. The raw value is only compared, never returned. */
export function riskTypeGroup(riskEventType) {
  if (typeof riskEventType !== 'string') return 'generic';
  for (const [group, types] of Object.entries(RISK_TYPE_GROUPS)) if (types.includes(riskEventType)) return group;
  return 'generic';
}
export const riskTypeLabel = (riskEventType) => RISK_TYPE_LABELS[riskTypeGroup(riskEventType)];

// The network owner of the sign-in IP (ENTRAID-346): classified by the collector from the published cloud ranges, locally,
// and sent as identifiers (never text), so the portal maps them to the labels below.
export const NETWORK_PROVIDERS = Object.freeze(['aws', 'gcp']);
export const NETWORK_KINDS = Object.freeze(['datacenter']);
export const NETWORK_PROVIDER_LABELS = Object.freeze({ aws: 'Amazon AWS', gcp: 'Google Cloud' });
export const NETWORK_KIND_LABELS = Object.freeze({ datacenter: 'data center network' });

/** "Amazon AWS (us-east-1), data center network"; null for anything that is not a known provider and kind. */
export function networkText(network) {
  if (network === null || typeof network !== 'object') return null;
  const provider = Object.hasOwn(NETWORK_PROVIDER_LABELS, network.provider) ? NETWORK_PROVIDER_LABELS[network.provider] : null;
  const kind = Object.hasOwn(NETWORK_KIND_LABELS, network.kind) ? NETWORK_KIND_LABELS[network.kind] : null;
  if (!provider || !kind) return null;
  const region = typeof network.region === 'string' && /^[a-z0-9-]{1,40}$/.test(network.region) ? ` (${network.region})` : '';
  return `${provider}${region}, ${kind}`;
}

/** The sentence for the "Named location" row. The name is the customer's own text; the caller renders it as text only. */
export function namedLocationText(location) {
  if (location === null || typeof location !== 'object' || typeof location.name !== 'string' || location.name.trim() === '') return null;
  return location.trusted === true
    ? `IP is in the trusted location "${location.name}"`
    : `IP is in the named location "${location.name}" (not marked trusted)`;
}

export const DATACENTER_HINT = 'Sign-ins from cloud networks (VPN, virtual desktops, automation) often look unfamiliar to Microsoft. If this is your own network, add it as a trusted named location in Microsoft Entra.';

export function evaluateIntegrity(record, id, ctx = {}) {
  const meta = typeof id === 'string' && AUDIT_INTEGRITY_IDS.includes(id) ? noticeMeta(id) : null;
  if (!meta) throw new Error(`evaluateIntegrity: not an audit-derived integrity id (${typeof id === 'string' ? id.slice(0, 64) : typeof id})`);
  const activity = normalizeActivityName(record?.activityDisplayName);
  const guardian = id === 'integrity.guardian-changed';
  const subject = guardian ? userRef(firstTarget(record, 'User'), ctx) : null;
  return {
    catalogId: id,
    category: 'integrity',
    severity: meta.severity,
    actor: actorOf(record, ctx),
    target: guardian ? (subject ?? { kind: 'user', directoryId: null, displayName: PLACEHOLDER.user }) : genericRef(record, 'app', 'ServicePrincipal'),
    subject,
    detail: { changedProperties: [], addedAliases: [], roleName: null, aggregateCount: 1, activity },
    flags: { watchedResource: false, selfBenefiting: false },
    audiences: ['guardians', 'admins'],
    extraOwnerIds: [],
    excludeRecipientIds: guardian && subject?.directoryId ? [subject.directoryId] : [],
  };
}

// ---- Sign-in method names for security-info events (ENTRAID-314) ----

// Free text from a record never reaches an event: every name maps to this
// allowlist by contained keyword, and anything else becomes OTHER_METHOD.
export const OTHER_METHOD = 'Other sign-in method';
const METHOD_RULES = Object.freeze([
  [/fido2?|passkey/, () => 'Passkey or FIDO2 security key'],
  [/windows hello/, () => 'Windows Hello for Business'],
  [/temporary access|\btap\b/, () => 'Temporary Access Pass'],
  [/hardware.*(oath|token)|(oath|token).*hardware/, () => 'Hardware OATH token'],
  [/software.*(oath|token)|(oath|token).*software|\boath\b/, () => 'Software OATH token'],
  [/authenticator/, () => 'Microsoft Authenticator'],
  [/phone|sms|voice/, () => 'Phone'],
  [/e-?mail/, () => 'Email'],
  [/certificate|x\.?509/, () => 'Certificate'],
  [/security question/, () => 'Security questions'],
  [/password/, () => 'Password'],
]);
const METHOD_REASON = /^(?:User|Admin) (?:registered|deleted|updated) (.+)$/i;
const GENERIC_REASONS = new Set(['security info', 'all required security info', 'default security info']);

function methodLabel(name) {
  const t = String(name).toLowerCase();
  for (const [re, label] of METHOD_RULES) if (re.test(t)) return label(t);
  return OTHER_METHOD;
}

function detailValue(record, key) {
  const list = Array.isArray(record?.additionalDetails) ? record.additionalDetails : [];
  const hit = list.find((d) => typeof d?.key === 'string' && d.key.toLowerCase() === key.toLowerCase() && typeof d.value === 'string' && d.value.trim() !== '');
  return hit ? hit.value.trim() : null;
}

/**
 * The distinct sign-in method names behind a set of raw security-info audit
 * records, in order of first appearance. Per record: additionalDetails
 * AuthenticationMethod, then AuthenticationMethodType, then the resultReason
 * suffix ("User registered Passkey"), unless that is the generic wording.
 * Each name is mapped to a fixed label (see METHOD_RULES), never passed through.
 * Returns [] when no record names a method.
 */
export function authenticationMethodsOf(records) {
  const out = [];
  const seen = new Set();
  for (const record of Array.isArray(records) ? records : []) {
    let name = detailValue(record, 'AuthenticationMethod') ?? detailValue(record, 'AuthenticationMethodType');
    if (!name) {
      const m = METHOD_REASON.exec(String(record?.resultReason ?? '').trim());
      if (m && !GENERIC_REASONS.has(m[1].trim().toLowerCase())) name = m[1].trim();
    }
    if (!name) continue;
    const label = methodLabel(name);
    const key = label.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(label);
  }
  return out;
}

const GENERIC_REGISTERED = /^user registered security info$/i;

/**
 * True for the record Entra writes when the combined security info prompt closes without a registration
 * (ENTRAID-347): "User registered security info" with exactly that generic result reason, no method in
 * additionalDetails and no changed property on any target. A real registration always has a companion record
 * that names the method; anything unexpected (another reason, any detail, any change) is not method-less.
 */
export function isMethodlessSecurityInfo(record) {
  if (normalizeActivityName(record?.activityDisplayName) !== 'User registered security info') return false;
  if (!GENERIC_REGISTERED.test(String(record?.resultReason ?? '').trim())) return false;
  if (detailValue(record, 'AuthenticationMethod') || detailValue(record, 'AuthenticationMethodType')) return false;
  const targets = Array.isArray(record?.targetResources) ? record.targetResources : [];
  return !targets.some((t) => Array.isArray(t?.modifiedProperties) && t.modifiedProperties.length > 0);
}

// One named method as the subject of a sentence ("A passkey ... was added"); null keeps the list wording.
const METHOD_NOUNS = Object.freeze({
  'Passkey or FIDO2 security key': ['A passkey or FIDO2 security key', false],
  'Windows Hello for Business': ['Windows Hello for Business', false],
  'Temporary Access Pass': ['A Temporary Access Pass', false],
  'Hardware OATH token': ['A hardware OATH token', false],
  'Software OATH token': ['A software OATH token', false],
  'Microsoft Authenticator': ['Microsoft Authenticator', false],
  Phone: ['A phone number', false],
  Email: ['An email address', false],
  Certificate: ['A certificate', false],
  'Security questions': ['Security questions', true],
  Password: ['A password', false],
});
/** [noun phrase, plural] for one sign-in method label, or null. */
export const methodNoun = (label) => (typeof label === 'string' && hasOwn(METHOD_NOUNS, label) ? METHOD_NOUNS[label] : null);

// ---- Wording (spec 6.1): "what changed" and the subject line's short form ----

const utc = (iso) => {
  // new Date(null) is the epoch; a missing or blank time must never read as 1970.
  if (iso === null || iso === undefined || (typeof iso === 'string' && iso.trim() === '')) return 'an unknown time';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? 'an unknown time' : `${d.toISOString().slice(0, 16).replace('T', ' ')} UTC`;
};
const w = (what, short) => ({ what, short });

/** The label of a case an administrator who did not receive the notice opened with a No (ENTRAID-347). */
export const REPORTED_BY_ADMIN = 'Reported by an admin';
const PLACEHOLDER_NAMES = new Set(Object.values(PLACEHOLDER));
const GAP_RULE = 'Nothing is lost yet: Watch catches up when it resumes, as long as the gap is shorter than Microsoft\'s audit retention for this tenant (7 days on Free, 30 days on P1 or P2).';


// ---- Who and what a notice is about (ENTRAID-348) -----------------------------------------------------------
// A record usually has several targets (the role AND the member, the group AND the owner), so the notice reads the
// principals from event.entra.targets, not only from event.subject (a user): a member can be a service principal or
// a group. An event stored before ENTRAID-348 has no targets and keeps the old wording.

const quote = (s) => `"${s}"`;
const realName = (v) => typeof v === 'string' && v.trim() !== '' && !PLACEHOLDER_NAMES.has(v);
const PRINCIPAL_TYPES = Object.freeze(['user', 'serviceprincipal', 'group']);

/** The principal a membership, ownership or role notice is about: { type, name, upn } or null. */
export function principalOf(event) {
  const targets = Array.isArray(event?.entra?.targets) ? event.entra.targets : [];
  const primaryId = event?.target?.directoryId ?? null;
  const kind = event?.target?.kind;
  const subjectId = event?.subject?.directoryId ?? null;
  const candidates = targets.filter((t) => {
    if (!PRINCIPAL_TYPES.includes(lower(t?.type))) return false;
    // The group or application the notice is about is not its own member or owner.
    if ((kind === 'group' || kind === 'app') && primaryId && t.id === primaryId) return false;
    if (kind === 'group' && lower(t.type) === 'group' && !primaryId) return false;
    return Boolean(t.displayName || t.userPrincipalName);
  });
  const hit = (subjectId && candidates.find((t) => t.id === subjectId)) || candidates[0];
  if (hit) return { type: lower(hit.type), name: realName(hit.displayName) ? hit.displayName : null, upn: realName(hit.userPrincipalName) ? hit.userPrincipalName : null };
  const s = event?.subject?.displayName;
  return realName(s) ? { type: 'user', name: s, upn: null } : null;
}

/** The principal as quoted text: a user by name and principal name, anything else with its kind. `lead` starts a sentence. */
function principalText(p, { lead = false } = {}) {
  if (!p) return null;
  const label = p.name && p.upn && lower(p.name) !== lower(p.upn) ? `${p.name} (${p.upn})` : (p.upn ?? p.name);
  if (p.type === 'serviceprincipal') return `${lead ? 'The' : 'the'} service principal ${quote(p.name ?? p.upn)}`;
  if (p.type === 'group') return `${lead ? 'The' : 'the'} group ${quote(p.name ?? p.upn)}`;
  return quote(label);
}
/** The short form for a subject line: a user by principal name, anything else by name, always in quotes. */
const principalShort = (p) => (p ? quote(p.type === 'user' ? (p.upn ?? p.name) : (p.name ?? p.upn)) : null);

/** The member of a group notice: the user's display name as before, or the quoted principal when it is not a user. */
function memberText(event, subjectName) {
  const p = principalOf(event);
  const subjectIsUser = event?.subject?.displayName && realName(event.subject.displayName);
  if (subjectIsUser && (!p || p.type === 'user')) return subjectName;
  return principalText(p, { lead: true }) ?? subjectName;
}

const FIELD_QUOTE = (v) => (v === null || v === undefined ? null : quote(v));
const MAX_SENTENCE_CHANGES = 4;

/** "Label: "old" -> "new"" for one change; a change with no value names only that it changed. */
export function changeText(c) {
  const [o, n] = [FIELD_QUOTE(c?.oldValue), FIELD_QUOTE(c?.newValue)];
  if (o && n) return `${c.property}: ${o} -> ${n}`;
  if (n) return `${c.property}: set to ${n}`;
  if (o) return `${c.property}: was ${o}`;
  return `${c.property} changed`;
}
const changesOfEvent = (event) => (Array.isArray(event?.entra?.changes) ? event.entra.changes : []);

/** The sentence with " Changed: ..." added when the collector sent changed values. */
function withChanges(result, event) {
  const all = changesOfEvent(event);
  if (all.length === 0) return result;
  const shown = all.slice(0, MAX_SENTENCE_CHANGES).map(changeText);
  const more = all.length > MAX_SENTENCE_CHANGES ? `; and ${all.length - MAX_SENTENCE_CHANGES} more` : '';
  return { ...result, what: `${result.what} Changed: ${shown.join('; ')}${more}.` };
}

const permissionList = (names) => {
  const shown = names.slice(0, 8).map(quote);
  const rest = names.length - shown.length;
  return `${shown.join(', ')}${rest > 0 ? ` and ${rest} more` : ''}`;
};
const CREDENTIAL_NAME = Object.freeze({ secret: 'client secret', certificate: 'certificate', unknown: 'credential' });
const day = (iso) => (typeof iso === 'string' && /^\d{4}-\d{2}-\d{2}/.test(iso) ? iso.slice(0, 10) : null);

function credentialSentence(event, t) {
  const list = Array.isArray(event?.entra?.credentials) ? event.entra.credentials : [];
  if (list.length === 0) return null;
  const removed = /^remove/i.test(event?.detail?.activity ?? '');
  const verb = removed ? 'removed from' : 'added to';
  if (list.length > 1) return `${list.length} credentials were ${verb} the application ${t}.`;
  const c = list[0];
  const name = CREDENTIAL_NAME[c.type] ?? 'credential';
  const description = realName(c.description) ? ` ${quote(c.description)}` : '';
  const expires = day(c.validUntil) ? ` (expires ${day(c.validUntil)})` : '';
  return `A ${name}${description}${expires} was ${verb} the application ${t}.`;
}

function riskDetails(event, forSelf) {
  const r = event?.risk;
  const label = riskTypeLabel(r?.riskEventType);
  if (!r) return label;
  const level = ['low', 'medium', 'high'].includes(r.riskLevel) ? `risk level ${r.riskLevel}` : null;
  const place = [r.city, r.countryOrRegion].filter((x) => typeof x === 'string' && x.trim()).join(', ');
  // The person reading about their own sign-in is never shown the IP address in the notice (ENTRAID-346); a guardian is.
  const ip = !forSelf && typeof r.ipAddress === 'string' && r.ipAddress ? `IP address ${r.ipAddress}` : null;
  return [label, level, place || null, ip].filter(Boolean).join('; ');
}

// ---- Monitored account sign-ins (2026-10-25) --------------------------------------
// The words of a monitored sign-in come from the allowlisted event.signIn block only (watch-callback-schema.mjs).
function signInOutcome(event) {
  const r = event?.signIn?.result;
  if (r === 'success') return 'signed in';
  if (r === 'interrupted') return 'started a sign-in that was interrupted (for example by a multifactor prompt)';
  if (r === 'failure') return 'tried to sign in and failed';
  return 'signed in';
}
function signInPlace(event) {
  const f = event?.signIn ?? {};
  const place = [f.city, f.countryOrRegion].filter((x) => typeof x === 'string' && x.trim()).join(', ');
  const app = typeof f.appDisplayName === 'string' && f.appDisplayName.trim() ? ` to ${quote(f.appDisplayName)}` : '';
  return `${app}${place ? ` from ${place}` : ''}`;
}
function signInWindow(event) {
  const f = event?.signIn ?? {};
  if (f.windowStart && f.windowEnd) return ` (${f.windowStart} to ${f.windowEnd})`;
  if (f.windowStart) return ` (from ${f.windowStart})`;
  if (f.windowEnd) return ` (until ${f.windowEnd})`;
  return ' (no window is set)';
}
const signInRisk = (event) => (['low', 'medium', 'high'].includes(event?.signIn?.riskLevelDuringSignIn) ? event.signIn.riskLevelDuringSignIn : 'elevated');

export function describeEvent(event, { forSelf = false } = {}) {
  const a = event?.detail?.activity ?? '';
  // A real item name is quoted so it stands out from the sentence; a placeholder ("an application") is not.
  const rawT = event?.target?.displayName;
  const t = typeof rawT === 'string' && rawT.trim() && !PLACEHOLDER_NAMES.has(rawT) ? `"${rawT}"` : (rawT ?? 'a resource');
  const s = event?.subject?.displayName ?? 'a user';
  const n = Number(event?.detail?.aggregateCount ?? 1);
  const notice = event?.detail?.notice ?? {};
  const tenant = notice.tenantName ?? t;
  const acc = forSelf ? 'your account' : `the account of ${s}`;
  const Acc = forSelf ? 'Your account' : `The account of ${s}`;
  switch (event?.catalogId) {
    case 'self.security-info-added':
    case 'self.security-info-removed':
    case 'self.security-info-changed': {
      const all = event?.detail?.changedProperties ?? [];
      const methods = all.slice(0, 10);
      const many = methods.length > 1;
      const list = methods.length ? `: ${methods.join(', ')}${all.length > 10 ? ` and ${all.length - 10} more` : ''}` : '';
      const one = all.length === 1 ? methodNoun(all[0]) : null;
      if (event.catalogId === 'self.security-info-added' && all.length === 0 && event?.flags?.noMethodRecorded === true) {
        return w(`Microsoft recorded a security info prompt for ${acc}; no new sign-in method was registered.`,
          forSelf ? 'Security info prompt recorded for your account, no new method' : `Security info prompt recorded for ${s}, no new method`);
      }
      if (one && event.catalogId !== 'self.security-info-changed') {
        const [noun, plural] = one;
        const verb = event.catalogId === 'self.security-info-added' ? 'added to' : 'removed from';
        return w(`${noun} ${plural ? 'were' : 'was'} ${verb} ${acc}.`,
          forSelf ? `${noun} ${plural ? 'were' : 'was'} ${verb} your account` : `${all[0]} ${verb === 'added to' ? 'added for' : 'removed for'} ${s}`);
      }
      if (event.catalogId === 'self.security-info-added') {
        return w(`${many ? 'Sign-in methods were' : 'A sign-in method was'} added to ${acc}${list}.`,
          forSelf ? `${many ? 'Sign-in methods were' : 'A sign-in method was'} added to your account${list}` : `${many ? 'Sign-in methods' : 'Sign-in method'} added for ${s}${list}`);
      }
      if (event.catalogId === 'self.security-info-removed') {
        return w(`${many ? 'Sign-in methods were' : 'A sign-in method was'} removed from ${acc}${list}.`,
          forSelf ? `${many ? 'Sign-in methods were' : 'A sign-in method was'} removed from your account${list}` : `${many ? 'Sign-in methods' : 'Sign-in method'} removed for ${s}${list}`);
      }
      return w(`The sign-in methods of ${acc} were changed${list}.`, forSelf ? `Your sign-in methods were changed${list}` : `Sign-in methods changed for ${s}${list}`);
    }
    case 'self.per-user-mfa-changed':
      return withChanges(a === 'Disable Strong Authentication'
        ? w(`Multifactor authentication was turned off for ${acc}.`, forSelf ? 'Multifactor authentication was turned off for your account' : `MFA turned off for ${s}`)
        : w(`The multifactor authentication setting of ${acc} was changed.`, forSelf ? 'Your multifactor authentication setting was changed' : `MFA setting changed for ${s}`), event);
    case 'self.password-reset-by-other':
      return w(`The password of ${acc} was reset by someone else.`, forSelf ? 'Your password was reset by someone else' : `Password reset for ${s}`);
    case 'self.alias-added': {
      // Addresses leave the customer account for the Me audience only.
      const list = forSelf ? (event?.detail?.addedAliases ?? []).slice(0, 5) : [];
      return w(`An email address was added to ${acc}${list.length ? `: ${list.join(', ')}` : ''}.`, forSelf ? 'An email address was added to your account' : `Email address added for ${s}`);
    }
    case 'self.alternate-email-changed':
      return w(`The alternate email of ${acc} was changed.`, forSelf ? 'Your alternate email was changed' : `Alternate email changed for ${s}`);
    case 'self.account-state-changed':
      return a === 'Disable account'
        ? w(`${Acc} was disabled.`, forSelf ? 'Your account was disabled' : `Account disabled: ${s}`)
        : w(`${Acc} was enabled.`, forSelf ? 'Your account was enabled' : `Account enabled: ${s}`);
    case 'self.device-registered':
      return w(`The device ${t} was registered to ${acc}.`, forSelf ? 'A device was registered to your account' : `Device registered for ${s}`);
    case 'self.risky-sign-in': {
      const label = riskDetails(event, forSelf);
      return forSelf
        ? w(`A risky sign-in was detected on your account (${label}).`, 'Risky sign-in on your account')
        : w(`A risky sign-in was detected for ${s} (${label}).`, `Risky sign-in: ${s}`);
    }
    case 'monitored.break-glass-sign-in':
      return w(`The break-glass account ${s} ${signInOutcome(event)}${signInPlace(event)}. A break-glass account must never sign in outside an emergency.`, `Break-glass sign-in: ${s}`);
    case 'monitored.service-account-sign-in':
      return w(`The service account ${s} ${signInOutcome(event)} interactively${signInPlace(event)}. A service account should only be used by an application.`, `Interactive sign-in by service account ${s}`);
    case 'monitored.vendor-sign-in-outside-window':
      return w(`The vendor account ${s} ${signInOutcome(event)} outside its allowed window${signInWindow(event)}${signInPlace(event)}.`, `Vendor sign-in outside its window: ${s}`);
    case 'monitored.former-employee-sign-in':
      return w(`The former employee account ${s} ${signInOutcome(event)}${signInPlace(event)}. This account should no longer be used.`, `Former employee sign-in: ${s}`);
    case 'monitored.vip-risky-sign-in':
      return w(`The executive account ${s} ${signInOutcome(event)} with ${signInRisk(event)} sign-in risk${signInPlace(event)}.`, `Risky sign-in by executive ${s}`);
    case 'group.member-added':
      return n > 1 ? w(`${n} members were added to the group ${t}.`, `${n} members added to ${t}`) : w(`${memberText(event, s)} was added to the group ${t}.`, `Member added to ${t}`);
    case 'group.member-removed':
      return n > 1 ? w(`${n} members were removed from the group ${t}.`, `${n} members removed from ${t}`) : w(`${memberText(event, s)} was removed from the group ${t}.`, `Member removed from ${t}`);
    case 'group.owner-changed':
      return a === 'Add owner to group'
        ? w(`${memberText(event, s)} was made an owner of the group ${t}.`, `Owner added to ${t}`)
        : w(`${memberText(event, s)} was removed as an owner of the group ${t}.`, `Owner removed from ${t}`);
    case 'group.settings-changed': {
      // Old and new values, when the collector sent them, replace the bare list of property names.
      const props = changesOfEvent(event).length > 0 ? [] : (event?.detail?.changedProperties ?? []).slice(0, 10);
      return withChanges(w(`The settings of the group ${t} were changed${props.length ? `: ${props.join(', ')}` : ''}.`, `Settings changed for ${t}`), event);
    }
    case 'group.deleted-or-restored':
      if (a === 'Restore group') return w(`The group ${t} was restored.`, `Group restored: ${t}`);
      return w(`The group ${t} was ${a === 'Hard Delete group' ? 'permanently deleted' : 'deleted'}.`, `Group deleted: ${t}`);
    case 'group.license-changed':
      return w(`The licenses of the group ${t} were changed.`, `Licenses changed for ${t}`);
    case 'app.credential-added':
      return w(credentialSentence(event, t) ?? `A credential was added to the application ${t}.`, `Credential added to ${t}`);
    case 'app.permission-granted': {
      const g = event?.entra?.grant;
      const names = g?.permissions ?? [];
      const to = g?.grantee ? quote(g.grantee) : t;
      if (names.length === 0) return w(`An application permission was granted to ${to}.`, `Permission granted to ${to}`);
      const on = g.resource ? ` on ${quote(g.resource)}` : '';
      return names.length > 1
        ? w(`The application permissions ${permissionList(names)}${on} were granted to ${to}.`, `Permissions granted to ${to}`)
        : w(`The application permission ${quote(names[0])}${on} was granted to ${to}.`, `Permission granted to ${to}`);
    }
    case 'app.consent-granted': {
      const g = event?.entra?.grant;
      const names = g?.permissions ?? [];
      const actorName = event?.actor?.kind === 'user' ? (event.actor.userPrincipalName ?? event.actor.displayName) : null;
      const by = realName(actorName) ? ` by ${quote(actorName)}` : '';
      const who = g?.allUsers === true ? ' for all users' : (g?.allUsers === false ? ' for one user' : '');
      const list = names.length > 0 ? ` Permissions: ${permissionList(names)}.` : '';
      return w(`Consent was granted to the application ${t}${by}${who}.${list}`, `Consent granted to ${t}`);
    }
    case 'app.owner-changed': {
      const p = principalText(principalOf(event), { lead: true });
      if (!p) return w(`The owners of the application ${t} were changed.`, `Owner changed for ${t}`);
      return /^Remove/.test(a)
        ? w(`${p} was removed as an owner of the application ${t}.`, `Owner removed from ${t}`)
        : w(`${p} was added as an owner of the application ${t}.`, `Owner added to ${t}`);
    }
    case 'app.registered':
      return w(`The application ${t} was added to the tenant.`, `Application added: ${t}`);
    case 'role.assigned': {
      if (forSelf) return w(`You were assigned the directory role ${t}.`, 'A directory role was assigned to you');
      const p = principalOf(event);
      return w(`${principalText(p, { lead: true }) ?? s} was assigned the directory role ${t}.`, `Role assigned: ${t}${p ? ` to ${principalShort(p)}` : ''}`);
    }
    case 'role.removed': {
      if (forSelf) return w(`You were removed from the directory role ${t}.`, 'A directory role was removed from you');
      const p = principalOf(event);
      return w(`${principalText(p, { lead: true }) ?? s} was removed from the directory role ${t}.`, `Role removed: ${t}${p ? ` from ${principalShort(p)}` : ''}`);
    }
    case 'pim.activation':
      return w(`${event?.actor?.displayName ?? s} activated the directory role ${t}.`, `Role activated: ${t}`);
    case 'tenant.domain-changed':
      return withChanges(w(`A domain change was made (${a}): ${t}.`, `Domain changed: ${t}`), event);
    case 'tenant.federation-changed':
      return withChanges(w(`The authentication settings of the domain ${t} were changed.`, `Federation changed: ${t}`), event);
    case 'tenant.conditional-access-changed':
      return withChanges(w(`A Conditional Access setting was changed (${a}): ${t}.`, `Conditional Access changed: ${t}`), event);
    case 'tenant.auth-methods-policy-changed':
      return withChanges(w(`The authentication methods policy${t.startsWith('"') ? ` ${t}` : ''} was changed (${a}).`, 'Authentication methods policy changed'), event);
    case 'tenant.partner-changed':
      return w(`A partner relationship of the tenant was changed (${a})${t.startsWith('"') ? `: ${t}` : ''}.`, 'Partner relationship changed');
    case 'guest.invited': {
      const invited = changesOfEvent(event).find((c) => c.property === 'Invited email address' && realName(c.newValue))?.newValue;
      return w(`The guest ${t} was invited to the tenant${invited ? ` (invited address ${quote(invited)})` : ''}.`, `Guest invited: ${t}`);
    }
    case 'integrity.guardian-changed':
      if (notice.oldAddress) return w(`The address of guardian ${s} changed from ${notice.oldAddress} to ${notice.newAddress}. Both receive notices until an admin confirms.`, `Guardian address changed: ${s}`);
      return w(`The account of guardian ${s} changed (${a}).`, `Guardian account changed: ${s}`);
    case 'integrity.onguard-access-changed':
      return w(`onGuard's access to ${tenant} was changed (${a}).`, 'onGuard access changed');
    case 'integrity.collector-silent':
      return notice.resumed
        ? w(`Watch hears from ${tenant} again. No check ran between ${utc(notice.since)} and ${utc(notice.until)}; Watch is catching up.`, 'Watch resumed')
        : w(`Watch has not heard from ${tenant} since ${utc(notice.since)}. Changes after that time are not being checked. ${GAP_RULE}`, 'Watch has stopped hearing from your tenant');
    case 'integrity.collector-blind':
      return w(`onGuard can no longer read the audit log of ${tenant} since ${utc(notice.since)}. Changes after that time are not being checked.`, 'onGuard cannot read your audit log');
    case 'integrity.collector-lagging':
      return w(`Watch is behind for ${tenant}: the oldest change not yet checked is from ${utc(notice.since)}.`, 'Watch is behind');
    case 'integrity.watch-config-changed':
      return w(`Watch for ${tenant} was changed by ${notice.actor ?? 'an onGuard administrator'} at ${utc(notice.at)}: ${notice.summary ?? 'settings changed'}.${notice.agingImpact ? ` ${notice.agingImpact}` : ''}`, 'Watch settings changed');
    case 'integrity.volume-capped':
      return w(`More than ${notice.limit ?? 'the allowed number of'} notices were due in one hour for ${tenant}. The rest go to the next digest; critical notices still arrive immediately.`, 'Notification volume capped');
    case 'integrity.break-glass-unread': {
      const count = Number.isInteger(notice.count) && notice.count > 0 ? notice.count : 1;
      return w(`Watch could not read all sign-ins of ${count === 1 ? 'a break-glass account' : `${count} break-glass accounts`} for ${tenant}. A sign-in of ${count === 1 ? 'that account' : 'those accounts'} may not have been reported yet; Watch reads them again on its next run.`, 'Break-glass sign-ins not fully read');
    }
    case 'integrity.recipient-complained':
      return w(`The address ${notice.address ?? 'of a recipient'} reported a Watch account security notice as spam. Watch no longer sends account security notices to it; the person's account changes still appear in the portal.`, 'A recipient reported a notice as spam');
    case 'case.opened':
      if (notice.openedBy) return w(`A case was opened by ${notice.openedBy} about "${notice.eventWhat ?? 'a change'}".`, 'Case opened');
      // ENTRAID-347: an administrator who did not receive the notice answered No; the email says so.
      if (notice.reportedByAdmin === true) return w(`${REPORTED_BY_ADMIN}. A case was opened: ${notice.answeredBy ?? 'an administrator'} answered No to "${notice.eventWhat ?? 'a change'}" without having received the notice.${notice.comment ? ` Their comment: "${notice.comment}"` : ''}`, `Case opened, ${REPORTED_BY_ADMIN.toLowerCase()}`);
      return w(`A case was opened: ${notice.answeredBy ?? 'a recipient'} answered No to "${notice.eventWhat ?? 'a change'}".${notice.comment ? ` Their comment: "${notice.comment}"` : ''}`, 'Case opened');
    case 'case.updated':
      return w(`The case about "${notice.eventWhat ?? 'a change'}" is now ${notice.state ?? 'updated'}${notice.outcome ? ` (${notice.outcome})` : ''}.`, 'Case updated');
    case 'event.marked-expected':
      // The whole story: what happened, why it matters, what to do (the guardian may not have received the notice).
      return w(`${notice.markedBy ?? 'An onGuard administrator'} marked "${notice.eventWhat ?? 'a change'}" as expected at ${utc(notice.at)}. Nobody had answered "Was this expected?" yet, so this closed the question for everyone who had not answered, and the change now counts as expected. If you did not expect this change, open it in onGuard and flag it as unexpected, or answer No from your own notice about it: a No always opens a case for follow-up.`, 'Change marked as expected by an admin');
    case 'prefs.changed':
      return w(`Your Watch notification preferences for ${tenant} were changed.`, 'Your notification preferences changed');
    default:
      return w('A change was detected in your directory.', 'Change detected');
  }
}
